import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc, getDocFromServer } from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import {
  hasPermission as hasPermissionUtil,
  canAccessRoute as canAccessRouteUtil,
  allowedPermissions as allowedPermissionsUtil,
} from '../constants/adminPermissions';

const AdminPermissionsContext = createContext(null);

/** Normalize `_config/superAdmin` uid (trim; empty → null). */
function normalizeSuperAdminUid(data) {
  if (!data) return null;
  const raw = data.uid ?? data.userId ?? data.UID;
  if (raw == null) return null;
  const t = String(raw).trim();
  return t || null;
}

function tokenHasAdminClaim(claims) {
  if (!claims) return false;
  const a = claims.admin;
  return a === true || a === 'true';
}

export function AdminPermissionsProvider({ children }) {
  const [user] = useAuthState(auth);
  const [permissions, setPermissions] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  /** Firestore `_config/superAdmin.uid` after trim, or null if unset. */
  const [superAdminFirestoreUid, setSuperAdminFirestoreUid] = useState(null);
  /** Set when `_config/superAdmin` read fails (rules, network, App Check). */
  const [superAdminReadError, setSuperAdminReadError] = useState(null);
  /** True after a successful read when the super-admin document exists (even if uid field is missing). */
  const [superAdminDocExists, setSuperAdminDocExists] = useState(false);
  /** Matches Firestore create rule: token admin or an `admins/{uid}` document. */
  const [mayConfigureSuperAdminDoc, setMayConfigureSuperAdminDoc] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setPermissions(null);
      setIsSuperAdmin(false);
      setSuperAdminFirestoreUid(null);
      setSuperAdminReadError(null);
      setSuperAdminDocExists(false);
      setMayConfigureSuperAdminDoc(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (!cancelled) {
        setSuperAdminReadError(null);
        setSuperAdminFirestoreUid(null);
        setIsSuperAdmin(false);
        setSuperAdminDocExists(false);
        setMayConfigureSuperAdminDoc(false);
      }
      const superRef = doc(firestore, '_config', 'superAdmin');
      try {
        const tr = await user.getIdTokenResult(true);
        if (cancelled) return;
        if (tokenHasAdminClaim(tr.claims)) {
          let configuredUid = null;
          let readErr = null;
          let docExists = false;
          try {
            const superSnap = await getDocFromServer(superRef);
            docExists = superSnap.exists();
            configuredUid = docExists ? normalizeSuperAdminUid(superSnap.data()) : null;
          } catch (e) {
            readErr = e?.message || String(e);
            console.error('AdminPermissionsContext superAdmin read:', e);
          }
          if (!cancelled) {
            setPermissions(['full']);
            setMayConfigureSuperAdminDoc(true);
            setSuperAdminDocExists(docExists);
            setSuperAdminFirestoreUid(configuredUid);
            setSuperAdminReadError(readErr);
            setIsSuperAdmin(Boolean(configuredUid && configuredUid === user.uid));
            setLoading(false);
          }
          return;
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error reading admin token claims:', err);
        }
      }
      try {
        const [adminSnap, superSnap] = await Promise.all([
          getDoc(doc(firestore, 'admins', user.uid)),
          getDocFromServer(superRef),
        ]);
        if (cancelled) return;
        setPermissions(adminSnap.exists() ? (adminSnap.data()?.permissions ?? null) : null);
        setMayConfigureSuperAdminDoc(adminSnap.exists());
        const docExists = superSnap.exists();
        setSuperAdminDocExists(docExists);
        const configuredUid = docExists ? normalizeSuperAdminUid(superSnap.data()) : null;
        setSuperAdminFirestoreUid(configuredUid);
        setSuperAdminReadError(null);
        setIsSuperAdmin(Boolean(configuredUid && configuredUid === user.uid));
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading admin permissions:', err);
          setPermissions(null);
          setIsSuperAdmin(false);
          setSuperAdminFirestoreUid(null);
          setSuperAdminDocExists(false);
          setMayConfigureSuperAdminDoc(false);
          setSuperAdminReadError(err?.message || String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  /**
   * Offer "claim / repair super admin" when:
   * - super-admin doc read succeeded,
   * - user may write per rules (Identity admin claim or `admins/{uid}` exists),
   * - either no doc yet (create) or doc has no usable uid (repair update).
   */
  const canClaimSuper =
    Boolean(user) &&
    mayConfigureSuperAdminDoc &&
    superAdminReadError == null &&
    (!superAdminDocExists || superAdminFirestoreUid == null);

  const value = {
    permissions,
    isSuperAdmin,
    superAdminFirestoreUid,
    superAdminReadError,
    superAdminDocExists,
    canClaimSuper,
    loading,
    hasPermission: (permission) => hasPermissionUtil(permissions, permission),
    canAccessRoute: (path) => {
      if (isSuperAdmin && path === 'users') return true;
      return canAccessRouteUtil(permissions, path);
    },
    allowedPermissions: allowedPermissionsUtil(permissions),
  };

  return (
    <AdminPermissionsContext.Provider value={value}>
      {children}
    </AdminPermissionsContext.Provider>
  );
}

export function useAdminPermissions() {
  const ctx = useContext(AdminPermissionsContext);
  if (ctx == null) {
    throw new Error('useAdminPermissions must be used within AdminPermissionsProvider');
  }
  return ctx;
}
